import { ComponentPropsWithoutRef, forwardRef } from "react";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import cx from "classnames";

import { Text } from "../Text";
import { DialogCancelButton } from "./DialogCancelButton";

import styles from "./Dialog.module.scss";
import { childrenToString } from "../../internal/functions";
import { useTrackingId, useMergeRefs } from "../../hooks";
import { DataTrackingId } from "../../types";
import { useDialogContext } from "./internal/useDialogContext";

/**
 * Props for the DialogHeader component
 * @extends ComponentPropsWithoutRef<"header">
 */
export type DialogHeaderProps = ComponentPropsWithoutRef<"header"> &
  DataTrackingId;

/**
 * DialogHeader component for the header section of a Dialog.
 *
 * Features:
 * - Displays dialog title with headline typography
 * - Includes close button with proper accessibility
 * - Sticky positioning for long content
 * - Consistent spacing and styling
 * - Automatic focus management
 *
 * @example
 * <Dialog.Header>Confirm Delete</Dialog.Header>
 */
export const DialogHeader = forwardRef<HTMLDivElement, DialogHeaderProps>(
  (props: DialogHeaderProps, ref) => {
    const { children, className, ...rest } = props;
    const { headerRef } = useDialogContext();
    const data = {
      children: childrenToString(props.children),
    };

    const trackingId = useTrackingId({
      name: "Dialog",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const headerClassNames = cx(className, styles["header"]);

    return (
      <header
        className={headerClassNames}
        data-anv="dialog-header"
        data-tracking-id={trackingId}
        ref={useMergeRefs([ref, headerRef])}
        {...rest}
      >
        <Text variant="headline" el="h1">
          {children}
        </Text>
        <span className={styles["close-container"]}>
          <DialogCancelButton
            className={styles["close-button"]}
            size="small"
            appearance="ghost"
            aria-label="Close dialog"
            icon={Close}
          />
        </span>
      </header>
    );
  },
);

DialogHeader.displayName = "DialogHeader";
